class Menu {
	private readonly mouseOutHideDelay: number = 50;
	private readonly wrapper: HTMLElement;
	private static menuLUID = 0;

	constructor(private readonly menu: HTMLElement) {
		// Allows for non-js fallbacks
		menu.classList.add('js-ready');
		this.wrapper = menu.closest("nav") ?? menu;
		this.hydrateMenuSubtree(menu);
		this.loadPotentialAJAXMenu(menu); // Only run if on mobile
	}

	private hydrateMenuSubtree(menuSubtreeRoot: HTMLElement) {
		this.createButtons(menuSubtreeRoot);
		this.setupEventListeners(menuSubtreeRoot);
		Menu.menuLUID++;
	}

	private createButtonTemplate() {
		// Creates a button to be cloned later
		const icon = document.createElement('span');
		icon.classList.add('icon');
		icon.setAttribute('aria-hidden', 'true');

		const text = document.createElement('span');
		text.classList.add('text', 'sr-desktop');
		text.innerHTML = '<span class="screen-reader-text">submenu</span>';

		const button = document.createElement('button');
		button.setAttribute('aria-expanded', 'false');
		button.append(text, icon);

		return button;
	}
	private createButtons(menuSubtreeRoot: HTMLElement): void {
		const buttonTemplate = this.createButtonTemplate();

		const kebabCaseRegexes = [/([a-z])([A-Z])/g, /[\s_]+/g] as const;
		for (const item of menuSubtreeRoot.querySelectorAll('.menu-item-has-children')) {
			const anchor = item.querySelector('a');
			if (!anchor) {
				continue;
			}

			// gets properties for aria attributes
			const name = anchor.textContent ?? '';

			const kebabCaseName = name
				.replace(kebabCaseRegexes[0], "$1-$2")
				.replace(kebabCaseRegexes[1], '-')
				.toLowerCase();
			const menuId = `menu-${kebabCaseName}-${Menu.menuLUID}`;
			const labelId = `label-${kebabCaseName}-${Menu.menuLUID}`;

			anchor.setAttribute('id', labelId);
			anchor.setAttribute('aria-expanded', 'false');

			const submenu = item.querySelector('.sub-menu');
			if (submenu) {
				submenu.setAttribute('id', menuId);
				submenu.setAttribute('aria-labelledby', labelId);
			}

			const button = buttonTemplate.cloneNode(true) as HTMLButtonElement;
			button.setAttribute('aria-controls', menuId);
			button.querySelector('.text')?.prepend(name + ' ');

			item.insertBefore(button, submenu);
		}
	}

	private setupEventListeners(menuSubtreeRoot: HTMLElement) {
		this.wrapper.addEventListener("plaudit__hideSubmenus", () => {
			for (const button of this.menu.querySelectorAll('button[aria-expanded="true"]')) {
				const buttonWrapper = button.closest('li');
				if (buttonWrapper) {
					this.submenuHide(buttonWrapper);
				}
			}
		});
		this.wrapper.addEventListener("plaudit__showSubmenus", () => {
			for (const element of this.menu.querySelectorAll('li[class*="current"]')) {
				this.submenuShow(element);
			}
		});

		for (const item of menuSubtreeRoot.querySelectorAll<HTMLElement>('.menu-item-has-children')) {
			let mouseOutTimer: any;

			// Show the menu on mouse hover of the trigger, Ignore on mobile
			item.addEventListener('pointerenter', event => {
				if (this.wrapper.ariaModal === 'true') return;

				this.submenuShow(event.target as Element);
				clearTimeout(mouseOutTimer);
			});

			/* Hide the menu when mouse hover leaves both the trigger and menu fly-out, but only after a short delay to help people jerky mouse movements (like those using head/eye trackers), Ignore on mobile */
			item.addEventListener('pointerleave', event => {
				if (this.wrapper.ariaModal === 'true') return;

				mouseOutTimer = setTimeout(() => {
					this.submenuHide(event.target as Element);
				}, this.mouseOutHideDelay);
			});

			// Hide the menu when the user tabs out or escapes, Ignore tabs on mobile
			item.addEventListener('keydown', event => {
				let currentItem: HTMLElement|null|undefined = item;
				if (event.key === 'Escape') {
					// allows for deep backing out
					if (item.querySelector('a')?.ariaExpanded === 'false') {
						currentItem = item.parentElement?.closest('li');
						if (!currentItem) {
							return;
						}
					}
					event.stopPropagation();
					this.submenuHide(currentItem);
					currentItem.querySelector('button')?.focus();
				}

				if (this.menu.ariaModal === 'true') return;

				const lastItem = currentItem.querySelector(":scope > .sub-menu > li:last-child a");
				const firstItem = currentItem.querySelector(":scope > .sub-menu > li:first-child a");
				if (
					(event.key === 'Tab' && !event.shiftKey && event.target === lastItem) ||
					(event.key === 'Tab' && event.shiftKey && event.target === firstItem)
				) {
					this.submenuHide(currentItem);
				}
			});

			// Toggle the menu when the trigger is activated
			for (const button of item.querySelectorAll<HTMLButtonElement>(':scope > button')) {
				button.addEventListener('click', event => {
					if (event.target instanceof HTMLElement) {
						this.submenuToggle(event.target.closest('li'));
					}
				});
			}
		}
	}

	private submenuHide(item: Element): void {
		for (const element of item.querySelectorAll(':scope > a, :scope > button')) {
			element.setAttribute('aria-expanded', 'false');
		}
	}
	private submenuShow(item: Element): void {
		for (const element of item.querySelectorAll(':scope > a, :scope > button')) {
			element.setAttribute('aria-expanded', 'true');
		}
	}
	private submenuToggle(item: Element|null): void {
		if (!item) {
			return;
		}
		if (item.querySelector('a')?.getAttribute('aria-expanded') === 'true') {
			this.submenuHide(item);
		} else {
			this.submenuShow(item);
		}
	}

	private loadPotentialAJAXMenu(menuRoot: HTMLElement) {
		// AJAX load a new version of the HTML for wrapper
		const container = menuRoot.querySelector('div[data-plaudit-menu-url]');
		if (!container) {
			return;
		}

		const menuURL = container.getAttribute('data-plaudit-menu-url');
		if (menuURL) {
			fetch(menuURL).then(response => {
				if (response.ok) {
					response.text().then(text => {
						const ajaxMenu = (new DOMParser()).parseFromString(text, "text/html").body.firstElementChild as HTMLElement;

						// Loaded menu does not have the current item marked with a class, so we need to do that
						// by looking for a link with the current URL, and if that doesn't exist then the first link
						// with the current URL but excluding the fragment identifier
						// NOTE: When developing locally this will not work on port 3000 since response comes back with 8443
						const currentURL = window.location.href;
						const currentLink = ajaxMenu.querySelector(`a[href="${currentURL}"], a[href="${currentURL.split('#')[0]}"]`);
						if (currentLink) {
							const currentItem = currentLink.closest('li');
							if (currentItem) {
								// Current
								currentItem.classList.add('current-menu-item');

								let parent = currentItem.parentNode;
								let parentFound = false;
								while (parent instanceof HTMLElement && parent !== ajaxMenu) {
									if (parent.classList.contains('sub-menu')) {
										if (!parentFound) {
											// Parent
											parentFound = true;
											parent.classList.add('current-menu-parent');
										}

										// Ancestor
										parent.classList.add('current-menu-ancestor');
									}
									parent = parent.parentNode;
								}
							}
						}

						// Treat as new menu
						// First the first item marked current.
						let currentItem: Element|null|undefined = null;
						for (const selector of ['.current-menu-item > ul > .current-menu-item > a',    // First find 'Overview' menu item
							'.current-menu-item > :is(button, a)']) {             // Then a link or button
							currentItem = ajaxMenu.querySelector(selector);
							if (currentItem)
								break;
						}

						if (currentItem) {
							// Open all ancestors
							let parent = currentItem.parentNode;
							while (parent instanceof HTMLElement && parent !== ajaxMenu) {
								if (parent.classList.contains('sub-menu')) {
									const button = parent.closest('li')?.querySelector('.mobile-menu-item--button');
									if (button) {
										this.submenuShow(button);
									}
								}
								parent = parent.parentNode;
							}
						}

						container.parentElement!.append(ajaxMenu);
						this.hydrateMenuSubtree(ajaxMenu);
					});
				} else {
					console.error('Failed to load menu HTML');
				}
			});
		}
	}
}
document.addEventListener("DOMContentLoaded", () => {
	for (const element of document.querySelectorAll<HTMLElement>(".wp-block-plaudit-menu:not(.js-ready)")) {
		new Menu(element);
	}
});
